<template>
  <modal 
    name="remove-insurance" 
    class="modal-inner" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    height="auto" 
    :scrollable="true" 
    :adaptive="true" 
    :reset="true"
    @before-open="beforeOpen"
  >
    <span class="close-button" @click="hide('remove-insurance')">
      <BaseIcon icon="times-circle" class="text-white"/>
    </span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("remove_insurance.title") }}
    </div>
    <div class="modal-body">
      <!-- Info Text -->
      <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <BaseIcon icon="exclamation-triangle" class="text-yellow-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700">
              {{ $t("remove_insurance.info_text") }}
            </p>
          </div>
        </div>
      </div>

      <!-- Agreement Info Display -->
      <div class="mb-4 p-3 bg-gray-50 rounded">
        <div class="text-sm">
          <div><strong>{{ $t("remove_insurance.agreement_number") }}:</strong> {{ currentAgreement?.agreement_number }}</div>
          <div><strong>{{ $t("remove_insurance.customer_name") }}:</strong> {{ currentAgreement?.customer_name }}</div>
        </div>
      </div>

      <!-- PDF Upload -->
      <div class="form-group mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          {{ $t("remove_insurance.upload_dismissal_form") }} <span v-if="!isBypassUser">*</span>
        </label>
        <input 
          type="file" 
          accept=".pdf"
          @change="handleFileUpload"
          ref="fileInput"
          class="w-full border border-gray-300 rounded-md px-3 py-2"
          :class="{ 'border-red-500': errors.file }"
        />
        <span v-if="selectedFile" class="text-xs text-gray-600 mt-1">
          {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
        </span>
        <span v-if="errors.file" class="text-red-500 text-xs mt-1 block">{{ errors.file }}</span>
      </div>
      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 mt-6">
        <button 
          class="btn-secondary-outline" 
          @click="hide('remove-insurance')"
        >
          {{ $t("user_actions.cancel") }}
        </button>
        <button 
          class="btn-red" 
          @click="submitRemoveInsurance"
          :disabled="isSubmitting"
        >
          <BaseIcon v-if="isSubmitting" icon="spinner" spin class="mr-2" />
          {{ $t("remove_insurance.submit") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 600;

export default {
  props: {
    agreement: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isSubmitting: false,
      selectedFile: null,
      errors: {
        file: ''
      },
      currentAgreement: null
    };
  },

  computed: {
    modalWidth() {
      return MODAL_WIDTH;
    },
    isBypassUser() {
      const user = this.$store.state.user;
      return user && user.username === 'amaper';
    },
  },

  methods: {
    beforeOpen(event) {
      // Get agreement from event params or prop
      this.currentAgreement = event.params?.agreement || this.agreement;
      
      // Reset form
      this.selectedFile = null;
      this.errors = { file: '' };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      
      // Validate file type
      if (file && file.type !== 'application/pdf') {
        this.errors.file = this.$t('remove_insurance.error_invalid_file_type');
        this.selectedFile = null;
        return;
      }
      
      // Validate file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file && file.size > maxSize) {
        this.errors.file = this.$t('remove_insurance.error_file_too_large');
        this.selectedFile = null;
        return;
      }
      
      this.selectedFile = file;
      this.errors.file = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    validate() {
      this.errors = { file: '' };
      
      if (!this.selectedFile && !this.isBypassUser) {
        this.errors.file = this.$t('remove_insurance.error_file_required');
        return false;
      }
      
      return true;
    },

    async submitRemoveInsurance() {
      if (!this.validate()) {
        return;
      }

      this.isSubmitting = true;

      try {

        const formData = new FormData();
        if (this.selectedFile) {
          formData.append('pdf', this.selectedFile);
        }
        if (this.isBypassUser) {
          formData.append('skip_email', 'true');
          formData.append('skip_pdf', 'true');
        }
        
        const response = await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreement/remove-insurance/${this.currentAgreement._id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        if(response.status === 200){
            this.handleSuccess(this.$t('remove_insurance.success_message'));
        }else{
            throw new Error(this.$t('remove_insurance.error_submission_failed'));
        }

        this.hide('remove-insurance');
        this.$emit('insurance-removed');
        
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    hide(id) {
      this.$modal.hide(id);
    }
  }
};
</script>
